import React, { useState, useEffect } from 'react';
import { dashboardAPI, clientesAPI, exportAPI, seedDemoData } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  formatCurrency, 
  getStatusColor, 
  translateEstado,
  getGravedadColor
} from '../lib/utils';
import { 
  DollarSign, 
  FileWarning, 
  FileText, 
  Building2, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Wallet,
  CheckSquare,
  Users,
  ArrowRight,
  Download,
  Database,
  Target,
  Settings
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendUp, color = 'slate', onClick }) => {
  const colorClasses = {
    slate: 'text-slate-600',
    orange: 'text-orange-500',
    green: 'text-green-600',
    red: 'text-red-500',
    blue: 'text-blue-600',
  };

  return (
    <Card 
      className={`border-slate-200 hover:border-slate-300 transition-all active:scale-[0.98] ${onClick ? 'cursor-pointer' : ''}`} 
      data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}
      onClick={onClick}
    >
      <CardContent className="p-4 lg:p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs lg:text-sm font-medium text-slate-500 mb-0.5 lg:mb-1 truncate">{title}</p>
            <p className={`text-xl lg:text-2xl font-bold tracking-tight ${colorClasses[color]} font-mono`}>
              {value}
            </p>
            {subtitle && (
              <p className="text-[10px] lg:text-xs text-slate-400 mt-0.5 lg:mt-1 truncate">{subtitle}</p>
            )}
          </div>
          <div className={`p-2 lg:p-2.5 rounded-lg bg-slate-100 flex-shrink-0`}>
            <Icon className={`w-4 h-4 lg:w-5 lg:h-5 ${colorClasses[color]}`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1 mt-2 lg:mt-3 text-[10px] lg:text-xs">
            {trendUp ? (
              <TrendingUp className="w-3 h-3 text-green-500" />
            ) : (
              <TrendingDown className="w-3 h-3 text-red-500" />
            )}
            <span className={trendUp ? 'text-green-600' : 'text-red-600'}>
              {trend}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [obrasResumen, setObrasResumen] = useState([]);
  const [actividad, setActividad] = useState([]);
  const [clientes, setClientes] = useState({});
  const [tendencias, setTendencias] = useState([]);
  const [objetivos, setObjetivos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [objetivosDialogOpen, setObjetivosDialogOpen] = useState(false);
  const [objetivosForm, setObjetivosForm] = useState({
    facturacion_anual: 150000,
    facturacion_mensual: 12500,
    facturacion_trimestral: 37500,
    obras_mes: 3,
    margen_objetivo: 35
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, obrasRes, actividadRes, clientesRes, tendenciasRes, objetivosRes] = await Promise.all([
        dashboardAPI.stats(),
        dashboardAPI.obrasResumen(),
        dashboardAPI.actividadReciente(),
        clientesAPI.list(),
        api.get('/dashboard/tendencias'),
        api.get('/dashboard/objetivos'),
      ]);
      
      setStats(statsRes.data);
      setObrasResumen(obrasRes.data);
      setActividad(actividadRes.data);
      setTendencias(tendenciasRes.data);
      setObjetivos(objetivosRes.data);
      
      if (objetivosRes.data?.objetivos) {
        setObjetivosForm(objetivosRes.data.objetivos);
      }
      
      // Create clients lookup
      const clientesMap = {};
      clientesRes.data.forEach(c => {
        clientesMap[c.id] = c.nombre;
      });
      setClientes(clientesMap);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDemo = async () => {
    try {
      const response = await seedDemoData();
      toast.success('Datos de demostración creados');
      loadData(); // Reload dashboard
    } catch (error) {
      if (error.response?.data?.message?.includes('Ya existen')) {
        toast.info('Ya existen datos en el sistema');
      } else {
        toast.error('Error al crear datos de demostración');
      }
    }
  };

  const handleExport = async (type) => {
    try {
      toast.info('Generando archivo Excel...');
      const filename = `${type}_serama_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      let response;
      switch(type) {
        case 'clientes': 
          response = await exportAPI.clientes();
          break;
        case 'presupuestos': 
          response = await exportAPI.presupuestos();
          break;
        case 'obras': 
          response = await exportAPI.obras();
          break;
        case 'facturas': 
          response = await exportAPI.facturas();
          break;
        case 'gastos': 
          response = await exportAPI.gastos();
          break;
        case 'completo': 
          response = await exportAPI.completo();
          break;
        default: 
          return;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Archivo Excel descargado correctamente');
    } catch (error) {
      console.error('Error exportando:', error);
      toast.error('Error al exportar datos');
    }
  };

  const handleSaveObjetivos = async () => {
    try {
      await api.put('/dashboard/objetivos', objetivosForm);
      toast.success('Objetivos actualizados');
      setObjetivosDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al guardar objetivos');
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-slate-200 rounded-lg shadow-lg">
          <p className="font-medium text-slate-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header - Mobile optimized */}
      <div className="flex flex-col gap-3">
        <div className="hidden lg:flex lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
            <p className="text-slate-500 text-sm mt-1">Vista general de tu negocio</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              onClick={handleSeedDemo}
              data-testid="seed-demo-btn"
            >
              <Database className="w-4 h-4 mr-2" />
              Cargar Demo
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-dropdown-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Exportar Excel
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('completo')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Informe Completo
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleExport('clientes')}>
                  Clientes
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('presupuestos')}>
                  Presupuestos
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('obras')}>
                  Obras
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('facturas')}>
                  Facturas
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('gastos')}>
                  Gastos
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button 
              onClick={() => navigate('/presupuestos')}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="new-presupuesto-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Nuevo Presupuesto
            </Button>
          </div>
        </div>
        
        {/* Mobile Quick Actions */}
        <div className="lg:hidden grid grid-cols-3 gap-2">
          <Button 
            variant="outline"
            className="h-12 flex-col gap-1 text-xs font-medium active:scale-95 transition-transform"
            onClick={() => navigate('/presupuestos')}
            data-testid="mobile-quick-presupuesto"
          >
            <FileText className="w-5 h-5 text-slate-600" />
            Presupuesto
          </Button>
          <Button 
            variant="outline"
            className="h-12 flex-col gap-1 text-xs font-medium active:scale-95 transition-transform"
            onClick={() => navigate('/gastos')}
            data-testid="mobile-quick-gasto"
          >
            <Wallet className="w-5 h-5 text-slate-600" />
            Gasto
          </Button>
          <Button 
            variant="outline"
            className="h-12 flex-col gap-1 text-xs font-medium active:scale-95 transition-transform"
            onClick={() => navigate('/facturas')}
            data-testid="mobile-quick-factura"
          >
            <DollarSign className="w-5 h-5 text-slate-600" />
            Factura
          </Button>
        </div>
      </div>

      {/* KPI Grid - 2 cols mobile, 4 cols desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <KPICard
          title="Cobros Pendientes"
          value={formatCurrency(stats?.cobros_pendientes || 0)}
          subtitle={`${stats?.facturas_vencidas || 0} facturas vencidas`}
          icon={DollarSign}
          color={stats?.facturas_vencidas > 0 ? 'orange' : 'slate'}
          onClick={() => navigate('/facturas')}
        />
        <KPICard
          title="Presupuestos"
          value={stats?.presupuestos_sin_respuesta || 0}
          subtitle="Sin respuesta"
          icon={FileText}
          color="blue"
          onClick={() => navigate('/presupuestos')}
        />
        <KPICard
          title="Obras Activas"
          value={stats?.obras_activas || 0}
          subtitle={`${stats?.obras_en_riesgo || 0} en riesgo`}
          icon={Building2}
          color={stats?.obras_en_riesgo > 0 ? 'orange' : 'green'}
          onClick={() => navigate('/obras')}
        />
        <KPICard
          title="Pagos Próximos"
          value={formatCurrency(stats?.pagos_proximos || 0)}
          subtitle="Esta semana"
          icon={Wallet}
          color="slate"
          onClick={() => navigate('/pagos')}
        />
      </div>

      {/* Secondary KPIs - Compact on mobile */}
      <div className="grid grid-cols-4 gap-2 lg:gap-4">
        <Card className="border-slate-200 active:scale-[0.98] transition-transform cursor-pointer" onClick={() => navigate('/clientes')}>
          <CardContent className="p-2 lg:p-4 text-center">
            <p className="text-lg lg:text-2xl font-bold text-slate-900 font-mono">{stats?.total_clientes || 0}</p>
            <p className="text-[10px] lg:text-xs text-slate-500 mt-0.5">Clientes</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 active:scale-[0.98] transition-transform cursor-pointer" onClick={() => navigate('/pendientes')}>
          <CardContent className="p-2 lg:p-4 text-center">
            <p className="text-lg lg:text-2xl font-bold text-slate-900 font-mono">{stats?.pendientes_activos || 0}</p>
            <p className="text-[10px] lg:text-xs text-slate-500 mt-0.5">Tareas</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 active:scale-[0.98] transition-transform cursor-pointer" onClick={() => navigate('/gastos')}>
          <CardContent className="p-2 lg:p-4 text-center">
            <p className="text-lg lg:text-2xl font-bold text-green-600 font-mono">{formatCurrency(stats?.gastos_mes || 0)}</p>
            <p className="text-[10px] lg:text-xs text-slate-500 mt-0.5">Gastos Mes</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 active:scale-[0.98] transition-transform cursor-pointer" onClick={() => navigate('/facturas')}>
          <CardContent className="p-2 lg:p-4 text-center">
            <p className="text-lg lg:text-2xl font-bold text-red-500 font-mono">{formatCurrency(stats?.total_vencidas || 0)}</p>
            <p className="text-[10px] lg:text-xs text-slate-500 mt-0.5">Vencido</p>
          </CardContent>
        </Card>
      </div>

      {/* Objetivos y Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Objetivos */}
        <Card className="border-slate-200" data-testid="objetivos-card">
          <CardHeader className="pb-2 lg:pb-3 px-4 lg:px-6">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base lg:text-lg font-semibold flex items-center gap-2">
                <Target className="w-4 h-4 lg:w-5 lg:h-5" />
                Objetivos
              </CardTitle>
              <Button 
                variant="ghost" 
                size="icon"
                className="h-8 w-8"
                onClick={() => setObjetivosDialogOpen(true)}
                data-testid="edit-objetivos-btn"
              >
                <Settings className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 lg:space-y-4 px-4 lg:px-6">
            {objetivos && (
              <>
                {/* Anual */}
                <div className="space-y-1.5 lg:space-y-2">
                  <div className="flex justify-between text-xs lg:text-sm">
                    <span className="text-slate-600">Facturación Anual</span>
                    <span className="font-medium font-mono text-[11px] lg:text-sm">
                      {formatCurrency(objetivos.progreso.facturacion_anual)} / {formatCurrency(objetivos.objetivos.facturacion_anual)}
                    </span>
                  </div>
                  <Progress value={Math.min(objetivos.porcentajes.anual, 100)} className="h-1.5 lg:h-2" />
                  <p className="text-[10px] lg:text-xs text-slate-400 text-right">{objetivos.porcentajes.anual}%</p>
                </div>
                
                {/* Trimestral */}
                <div className="space-y-1.5 lg:space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Trimestre</span>
                    <span className="font-medium">
                      {formatCurrency(objetivos.progreso.facturacion_trimestral)} / {formatCurrency(objetivos.objetivos.facturacion_trimestral)}
                    </span>
                  </div>
                  <Progress value={Math.min(objetivos.porcentajes.trimestral, 100)} className="h-2" />
                  <p className="text-xs text-slate-400 text-right">{objetivos.porcentajes.trimestral}%</p>
                </div>
                
                {/* Mensual */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Mes Actual</span>
                    <span className="font-medium">
                      {formatCurrency(objetivos.progreso.facturacion_mensual)} / {formatCurrency(objetivos.objetivos.facturacion_mensual)}
                    </span>
                  </div>
                  <Progress value={Math.min(objetivos.porcentajes.mensual, 100)} className="h-2" />
                  <p className="text-xs text-slate-400 text-right">{objetivos.porcentajes.mensual}%</p>
                </div>

                {/* Margen */}
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Margen Real</span>
                    <span className={`text-lg font-bold ${objetivos.progreso.margen_real >= objetivos.objetivos.margen_objetivo ? 'text-green-600' : 'text-orange-500'}`}>
                      {objetivos.progreso.margen_real}%
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">Objetivo: {objetivos.objetivos.margen_objetivo}%</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Gráfico de Tendencias */}
        <Card className="lg:col-span-2 border-slate-200" data-testid="tendencias-chart">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold">Tendencias (6 meses)</CardTitle>
          </CardHeader>
          <CardContent>
            {tendencias.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={tendencias}>
                  <defs>
                    <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorGastos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="mes" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="ingresos" 
                    name="Ingresos"
                    stroke="#22c55e" 
                    fillOpacity={1} 
                    fill="url(#colorIngresos)" 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="gastos" 
                    name="Gastos"
                    stroke="#ef4444" 
                    fillOpacity={1} 
                    fill="url(#colorGastos)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                Sin datos de tendencias
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Gráfico de Margen por mes */}
      {tendencias.length > 0 && (
        <Card className="border-slate-200" data-testid="margen-chart">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold">Margen por Mes</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={tendencias}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="margen" 
                  name="Margen"
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Obras Resumen */}
        <Card className="lg:col-span-2 border-slate-200" data-testid="obras-resumen-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">Resumen de Obras</CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/obras')}
                className="text-slate-500 hover:text-slate-900"
              >
                Ver todas
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {obrasResumen.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay obras registradas</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      <th className="pb-3 pr-4">Obra</th>
                      <th className="pb-3 pr-4">Estado</th>
                      <th className="pb-3 pr-4 text-right">Presupuesto</th>
                      <th className="pb-3 pr-4 text-right">Gastos</th>
                      <th className="pb-3 text-right">Margen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {obrasResumen.slice(0, 5).map((obra) => (
                      <tr 
                        key={obra.id} 
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/obras`)}
                      >
                        <td className="py-3 pr-4">
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{obra.titulo}</p>
                            <p className="text-xs text-slate-500">{obra.numero}</p>
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${getStatusColor(obra.estado)}`}>
                            {translateEstado(obra.estado)}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-right font-mono text-sm">
                          {formatCurrency(obra.presupuesto_previsto)}
                        </td>
                        <td className="py-3 pr-4 text-right font-mono text-sm">
                          {formatCurrency(obra.gastos_reales)}
                        </td>
                        <td className={`py-3 text-right font-mono text-sm font-medium ${obra.margen >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {formatCurrency(obra.margen)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actividad Reciente */}
        <Card className="border-slate-200" data-testid="actividad-reciente-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold">Actividad Reciente</CardTitle>
          </CardHeader>
          <CardContent>
            {actividad.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Sin actividad reciente</p>
              </div>
            ) : (
              <div className="space-y-4">
                {actividad.slice(0, 6).map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className={`p-1.5 rounded ${item.tipo === 'factura' ? 'bg-blue-100' : 'bg-orange-100'}`}>
                      {item.tipo === 'factura' ? (
                        <Receipt className="w-4 h-4 text-blue-600" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900 truncate">{item.descripcion}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded ${getStatusColor(item.estado)}`}>
                          {translateEstado(item.estado)}
                        </span>
                        {item.importe && (
                          <span className="text-xs font-mono text-slate-500">
                            {formatCurrency(item.importe)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog Objetivos */}
      <Dialog open={objetivosDialogOpen} onOpenChange={setObjetivosDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="objetivos-dialog">
          <DialogHeader>
            <DialogTitle>Configurar Objetivos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Facturación Anual (€)</Label>
              <Input
                type="number"
                value={objetivosForm.facturacion_anual}
                onChange={(e) => setObjetivosForm({ ...objetivosForm, facturacion_anual: parseFloat(e.target.value) || 0 })}
                data-testid="objetivo-anual-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Facturación Trimestral (€)</Label>
                <Input
                  type="number"
                  value={objetivosForm.facturacion_trimestral}
                  onChange={(e) => setObjetivosForm({ ...objetivosForm, facturacion_trimestral: parseFloat(e.target.value) || 0 })}
                  data-testid="objetivo-trimestral-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Facturación Mensual (€)</Label>
                <Input
                  type="number"
                  value={objetivosForm.facturacion_mensual}
                  onChange={(e) => setObjetivosForm({ ...objetivosForm, facturacion_mensual: parseFloat(e.target.value) || 0 })}
                  data-testid="objetivo-mensual-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Obras por Mes</Label>
                <Input
                  type="number"
                  value={objetivosForm.obras_mes}
                  onChange={(e) => setObjetivosForm({ ...objetivosForm, obras_mes: parseInt(e.target.value) || 0 })}
                  data-testid="objetivo-obras-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Margen Objetivo (%)</Label>
                <Input
                  type="number"
                  value={objetivosForm.margen_objetivo}
                  onChange={(e) => setObjetivosForm({ ...objetivosForm, margen_objetivo: parseFloat(e.target.value) || 0 })}
                  data-testid="objetivo-margen-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setObjetivosDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveObjetivos} className="bg-slate-900 hover:bg-slate-800" data-testid="save-objetivos-btn">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Missing import
const Receipt = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z"/>
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/>
    <path d="M12 17V7"/>
  </svg>
);

export default DashboardPage;
